var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { formatDistance } from "date-fns";
import { IdentifiedPatientState } from "healthex-common/consent.dtos";
import { pluralize } from "healthex-common/utils";
import { computed, observable } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
export class StudiesOverviewViewModel {
    constructor(container) {
        this.lastReceivedStatsInterval = null;
        this.studiesUIManager = container.studiesUIManager;
        this.organizationsManager = container.organizationsManager;
        this.studyRecruitmentManager = container.studyRecruitmentManager;
        this.studyManager = container.studyManager;
    }
    run() {
        this.lastReceivedStatsInterval = window.setInterval(() => this.handleForceUpdateStatsTimestamp(), 60 * 1000);
    }
    stop() {
        if (this.lastReceivedStatsInterval !== null) {
            window.clearInterval(this.lastReceivedStatsInterval);
        }
    }
    get studyWithdrawnOrCompleted() {
        return this.studiesUIManager.studyWithdrawnOrCompleted;
    }
    get overviewStats() {
        return this.studiesUIManager.analyticsStats;
    }
    get patientEngagement() {
        if (this.studiesUIManager.enrollmentMetrics === NOT_LOADED)
            return NOT_LOADED;
        if (this.studiesUIManager.enrollmentMetrics === null)
            return null;
        const { notOpened, patientOpenedNotCompleted, providerOpenedNotCompleted, patientCompleted, providerCompleted } = this.studiesUIManager.enrollmentMetrics;
        return {
            "Not opened": notOpened,
            Completed: patientCompleted.total + providerCompleted.total,
            "Opened, not\ncompleted": patientOpenedNotCompleted + providerOpenedNotCompleted,
        };
    }
    get consentBreakdown() {
        if (this.studiesUIManager.enrollmentMetrics === NOT_LOADED)
            return NOT_LOADED;
        if (this.studiesUIManager.enrollmentMetrics === null)
            return null;
        const { patientCompleted, providerCompleted } = this.studiesUIManager.enrollmentMetrics;
        return {
            No: patientCompleted.declined + providerCompleted.declined,
            "I'm interested": patientCompleted.interested + providerCompleted.interested,
            Yes: patientCompleted.consented + providerCompleted.consented,
        };
    }
    get whatsNew() {
        return this.studiesUIManager.whatsNew;
    }
    get whatsNewFormatted() {
        if (this.whatsNew === NOT_LOADED || this.whatsNew === null) {
            return [];
        }
        const whatsNew = [];
        this.whatsNew.patientConsentCounts.forEach((consentCount) => {
            const hasConsented = consentCount.consentStatus === "OPTED_IN";
            const consentStatusText = hasConsented ? "consented to" : "declined";
            const consentTypeText = "enrollment";
            let recruitmentFilter = null;
            if (hasConsented) {
                recruitmentFilter = IdentifiedPatientState.ENROLLED;
            }
            else {
                recruitmentFilter = IdentifiedPatientState.DECLINED_ENROLLMENT;
            }
            whatsNew.push({
                id: crypto.randomUUID(),
                text: `${consentCount.count} ${pluralize(consentCount.count, "Patient", "Patients")} ${consentStatusText} ${consentTypeText}`,
                tab: "PATIENT_MANAGEMENT",
                recruitmentFilter,
            });
        });
        return whatsNew;
    }
    get whatsNewTotal() {
        if (this.whatsNew === NOT_LOADED || this.whatsNew === null) {
            return 0;
        }
        return this.whatsNewFormatted.length;
    }
    get lastStatsReceivedDate() {
        return this.studiesUIManager.lastStatsReceivedDate;
    }
    get lastUpdatedStats() {
        const _ = this.forceUpdateLastStatsReceived;
        return formatDistance(this.lastStatsReceivedDate, new Date(), { addSuffix: true });
    }
    get study() {
        return this.studiesUIManager.studyToManage;
    }
    get enrollmentInfo() {
        var _a;
        if (this.overviewStats === NOT_LOADED || this.overviewStats === null || this.study === NOT_LOADED || this.study === null) {
            return null;
        }
        const totalStats = this.overviewStats.totalStats;
        const targetNumberOfPatients = (_a = this.study.targetNumberOfPatients) !== null && _a !== void 0 ? _a : 0;
        let totalProgress = totalStats.ENROLLED / targetNumberOfPatients;
        totalProgress = Number.isFinite(totalProgress) ? totalProgress * 100 : 0;
        return {
            total: targetNumberOfPatients,
            enrolledFromText: this.getEnrolledFromText(this.overviewStats),
            alreadyEnrolledText: "Already enrolled",
            leftToEnrollText: this.getLeftToEnrollText(this.overviewStats, this.study),
            totalProgress: Math.min(100, totalProgress),
        };
    }
    get consentInfo() {
        if (this.overviewStats === NOT_LOADED || this.overviewStats === null || this.study === NOT_LOADED || this.study === null) {
            return null;
        }
        const stats = this.overviewStats.totalStats;
        const enrollmentRequestsSent = stats.ENROLLING + stats.ENROLLED + stats.DECLINED_ENROLLMENT;
        return {
            enrollmentRequestsSent,
            enrollmentPercentage: this.formatPercentage(stats.ENROLLED / enrollmentRequestsSent),
        };
    }
    get recruitmentInfo() {
        if (this.overviewStats === NOT_LOADED || this.overviewStats === null || this.study === NOT_LOADED || this.study === null) {
            return null;
        }
        const stats = this.overviewStats.totalStats;
        const excludedTotal = stats.DECLINED_ENROLLMENT + stats.REMOVED;
        const total = stats.ENROLLING + stats.ENROLLED + excludedTotal;
        return {
            identifiedPatientsText: `${total} identified ${this.getPatientPlural(total)}`,
            enrolling: stats.ENROLLING,
            enrolled: stats.ENROLLED,
            excluded: excludedTotal,
            enrollingPercentage: this.formatPercentage(stats.ENROLLING / total),
            enrolledPercentage: this.formatPercentage(stats.ENROLLED / total),
            excludedPercentage: this.formatPercentage(excludedTotal / total),
            chartLegendPosition: "none",
            chartData: this.getChartData(stats),
        };
    }
    getEnrolledFromText(overviewStats) {
        const totalStats = overviewStats.totalStats;
        return `${totalStats.ENROLLED} ${this.getPatientPlural(totalStats.ENROLLED)}`;
    }
    getLeftToEnrollText(overviewStats, study) {
        var _a;
        const leftToEnroll = Math.max(0, ((_a = study.targetNumberOfPatients) !== null && _a !== void 0 ? _a : 0) - overviewStats.totalStats.ENROLLED);
        return `${leftToEnroll} ${this.getPatientPlural(leftToEnroll)}`;
    }
    getChartData(stats) {
        return {
            Enrolling: stats.ENROLLING,
            Enrolled: stats.ENROLLED,
            Excluded: stats.DECLINED_PRESCREEN + stats.DECLINED_ENROLLMENT + stats.REMOVED,
        };
    }
    formatPercentage(value) {
        if (!Number.isFinite(value)) {
            return "0.00";
        }
        return (value * 100).toFixed(2);
    }
    handleForceUpdateStatsTimestamp() {
        this.forceUpdateLastStatsReceived = new Date();
    }
    getPatientPlural(length) {
        return pluralize(length, "patient", "patients");
    }
    whatsNewClicked(item) {
        this.studiesUIManager.setCurrentStudyManagerTab(item.tab, item.recruitmentFilter);
    }
}
__decorate([
    observable
], StudiesOverviewViewModel.prototype, "forceUpdateLastStatsReceived", void 0);
__decorate([
    computed
], StudiesOverviewViewModel.prototype, "studyWithdrawnOrCompleted", null);
__decorate([
    computed
], StudiesOverviewViewModel.prototype, "overviewStats", null);
__decorate([
    computed
], StudiesOverviewViewModel.prototype, "patientEngagement", null);
__decorate([
    computed
], StudiesOverviewViewModel.prototype, "consentBreakdown", null);
__decorate([
    computed
], StudiesOverviewViewModel.prototype, "whatsNew", null);
__decorate([
    computed
], StudiesOverviewViewModel.prototype, "whatsNewFormatted", null);
__decorate([
    computed
], StudiesOverviewViewModel.prototype, "lastStatsReceivedDate", null);
__decorate([
    computed
], StudiesOverviewViewModel.prototype, "lastUpdatedStats", null);
__decorate([
    computed
], StudiesOverviewViewModel.prototype, "study", null);
__decorate([
    computed
], StudiesOverviewViewModel.prototype, "enrollmentInfo", null);
__decorate([
    computed
], StudiesOverviewViewModel.prototype, "consentInfo", null);
__decorate([
    computed
], StudiesOverviewViewModel.prototype, "recruitmentInfo", null);
